import React, { useEffect, useState, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import LoadingOverlay from 'react-loading-overlay';
import { fetchGutAssessmentResult, fetchGutAssessmentHistory } from '../gutAssessment.service';
import gutAssessmentImages from '../gutAssessmentImages';
import GutRadarChart from './GutRadarChart';
import DraggableFloat from './DraggableFloat';
import './GutAssessmentResult.scss';

/**
 * Image component that tries to load the API image first,
 * falls back to local image if the API image fails to load.
 */
const ImageWithFallback = ({ src, fallbackSrc, alt = '', ...props }) => {
  const [imgSrc, setImgSrc] = useState(src || fallbackSrc);

  useEffect(() => {
    setImgSrc(src || fallbackSrc);
  }, [src, fallbackSrc]);

  const handleError = () => {
    if (imgSrc !== fallbackSrc) {
      setImgSrc(fallbackSrc);
    }
  };

  return <img src={imgSrc} onError={handleError} alt={alt} {...props} />;
};

const LEVEL_STYLES = [
  { color: '#F85269', gradient: 'linear-gradient(180deg, #F85269 0%, #FFDDE1 100%)' },  // level1
  { color: '#FD9153', gradient: 'linear-gradient(180deg, #FD9153 0%, #FFEFE5 100%)' },  // level2
  { color: '#F2C014', gradient: 'linear-gradient(180deg, #F2C014 0%, #FFF9DF 100%)' },  // level3
  { color: '#83C346', gradient: 'linear-gradient(180deg, #83C346 0%, #EEFEDF 100%)' },  // level4
  { color: '#60B072', gradient: 'linear-gradient(180deg, #60B072 0%, #EDF4EA 100%)' },  // level5
];

const LEVEL_COLORS = {
  level1: '#F85269',
  level2: '#FD9153',
  level3: '#F2C014',
  level4: '#83C346',
  level5: '#60B072',
};

/**
 * Get the level index (0-based) from suggestions based on score.
 * Suggestions are ordered from lowest to highest score range.
 */
const getLevelIndex = (suggestions, score) => {
  if (!suggestions || suggestions.length === 0) return 0;
  const index = suggestions.findIndex(
    (s) => score >= s.score_from && score <= s.score_to
  );
  return index >= 0 ? index : 0;
};

const getGradientByScore = (suggestions, score) => {
  const index = getLevelIndex(suggestions, score);
  return LEVEL_STYLES[index]?.gradient || LEVEL_STYLES[0].gradient;
};

const getLevelColorByScore = (suggestions, score) => {
  const index = getLevelIndex(suggestions, score);
  return LEVEL_STYLES[index]?.color || LEVEL_STYLES[0].color;
};

/**
 * Calculate the score for a specific group by summing the scores
 * of answered questions that belong to that group's question_ids.
 */
const calculateGroupScore = (group, questions) => {
  let score = 0;
  const questionIds = group.question_ids || [];

  questionIds.forEach((qId) => {
    const question = questions.find((q) => q._id === qId);
    if (question && question.answer && question.answer.length > 0) {
      const answerId = question.answer[0];
      const selectedOption = (question.question_value || []).find(
        (opt) => opt._id === answerId
      );
      if (selectedOption) {
        score += selectedOption.score || 0;
      }
    }
  });

  return score;
};

/**
 * Find the matching scoreDescription for a group given its score.
 */
const getGroupScoreDescription = (group, score) => {
  const descriptions = group.scoreDescriptions || [];
  return descriptions.find(
    (desc) => score >= desc.score_from && score <= desc.score_to
  );
};

/**
 * Find the matching overall suggestion based on total score.
 */
const getOverallSuggestion = (suggestions, totalScore) => {
  if (!suggestions || suggestions.length === 0) return null;
  return suggestions.find(
    (s) => totalScore >= s.score_from && totalScore <= s.score_to
  );
};

/**
 * Get the color for a group level based on level name from scoreDescriptions.
 */
const getGroupLevelColor = (levelName) => {
  switch (levelName) {
    case 'ยอดเยี่ยม':
      return LEVEL_COLORS.level5;
    case 'ดีมาก':
    case 'ดี':
      return LEVEL_COLORS.level4;
    case 'ปานกลาง':
      return LEVEL_COLORS.level3;
    case 'ควรปรับปรุง':
      return LEVEL_COLORS.level2;
    case 'ต้องได้รับการดูแล':
    default:
      return LEVEL_COLORS.level1;
  }
};

/**
 * Format a date string to dd/MM/yyyy
 */
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const GutAssessmentResult = () => {
  const history = useHistory();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const assessmentId = params.get('assessment_id') || '';
  const answerId = params.get('answer_id') || '';

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [expandedHints, setExpandedHints] = useState({});
  const [historyList, setHistoryList] = useState([]);
  const [selectedAnswerId, setSelectedAnswerId] = useState(answerId);

  useEffect(() => {
    const wrapper = document.getElementById('wrapper');
    if (wrapper) wrapper.style.maxWidth = '100%';
    return () => {
      if (wrapper) wrapper.style.maxWidth = '';
    };
  }, []);

  // Fetch history list
  useEffect(() => {
    if (assessmentId) {
      fetchGutAssessmentHistory(assessmentId)
        .then((data) => {
          if (data && data.length > 0) {
            setHistoryList(data);
          }
        })
        .catch((err) => console.error('History fetch error:', err));
    }
  }, [assessmentId]);

  // Fetch result when selectedAnswerId changes
  useEffect(() => {
    if (assessmentId && selectedAnswerId) {
      setLoading(true);
      setExpandedHints({});
      fetchGutAssessmentResult(assessmentId, selectedAnswerId)
        .then((data) => setResult(data))
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [assessmentId, selectedAnswerId]);

  const handleSelectHistory = (historyAnswerId) => {
    setSelectedAnswerId(historyAnswerId);
    setIsDropdownOpen(false);
  };

  // Compute all derived data from the API result
  const computedData = useMemo(() => {
    if (!result) return null;

    const questions = result.hw_assessments_questions || [];
    const groups = result.metaData?.groups || [];
    const suggestions = result.metaData?.suggestions || [];
    const listType = result.ListType || {};

    // Calculate total score from answered questions
    let totalScore = 0;
    questions.forEach((q) => {
      if (q.answer && q.answer.length > 0) {
        const answeredId = q.answer[0];
        const selectedOption = (q.question_value || []).find(
          (opt) => opt._id === answeredId
        );
        if (selectedOption) {
          totalScore += selectedOption.score || 0;
        }
      }
    });

    // Calculate per-group scores and find their matching level descriptions
    const groupResults = groups.map((group) => {
      const groupScore = calculateGroupScore(group, questions);
      const maxScore = (group.question_ids || []).length * 5; // Each question max score is 5
      const scoreDesc = getGroupScoreDescription(group, groupScore);

      return {
        name: group.name,
        systemName: group.system_name,
        score: groupScore,
        maxScore,
        level: scoreDesc?.level || '',
        description: scoreDesc?.description || '',
        hint: scoreDesc?.hint || '',
        color: getGroupLevelColor(scoreDesc?.level || ''),
        imageUrl: group.image_url || '',
      };
    });

    // Find overall suggestion based on total score
    const overallSuggestion = getOverallSuggestion(suggestions, totalScore);

    // Score range from the matched suggestion
    const scoreRange = overallSuggestion?.score_from != null && overallSuggestion?.score_to != null
      ? `คะแนน ${overallSuggestion.score_from}-${overallSuggestion.score_to}`
      : '';

    // Format the date for dropdown
    const resultDate = formatDate(result.updatedDate);

    return {
      totalScore,
      groupResults,
      overallSuggestion,
      scoreRange,
      resultDate,
      title: result.title || 'แบบประเมินสุขภาพลำไส้',
      suggestions,
      redirectImage: result.metaData?.redirectImage || '',
      redirectURL: result.metaData?.redirectURL || '',
    };
  }, [result]);

  // Calculate score difference compared to previous assessment
  const scoreDiff = useMemo(() => {
    if (!historyList.length || !selectedAnswerId || !computedData) return null;

    // Sort history by createdDate descending (newest first)
    const sorted = [...historyList].sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
    const currentIndex = sorted.findIndex((item) => item._id === selectedAnswerId);

    // If no previous entry exists (it's the oldest one), return null
    if (currentIndex === -1 || currentIndex >= sorted.length - 1) return null;

    const previousEntry = sorted[currentIndex + 1];
    const previousScore = previousEntry.totalScore || 0;
    const diff = computedData.totalScore - previousScore;

    return diff;
  }, [historyList, selectedAnswerId, computedData]);

  const handleBack = () => {
    history.push('/gut-assessment');
  };

  const toggleHint = (index) => {
    setExpandedHints((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  if (loading) return <div className="gut-result" data-testid="gut-result-loading"><LoadingOverlay text="Loading" spinner active={loading} styles={{ wrapper: { width: '100%', height: '100%', position: 'fixed', top: 0, left: 0, zIndex: 10 } }}><div /></LoadingOverlay></div>;
  if (error) return <div className="gut-result" data-testid="gut-result-error"><p>Error: {error}</p></div>;
  if (!result || !computedData) return <div className="gut-result" data-testid="gut-result-empty"><p>ไม่พบข้อมูลผลการประเมิน</p></div>;

  const { totalScore, groupResults, overallSuggestion, scoreRange, resultDate, title, suggestions, redirectImage, redirectURL } = computedData;
  const levelColor = getLevelColorByScore(suggestions, totalScore);

  // Get level key for icon mapping
  const levelIndex = getLevelIndex(suggestions, totalScore);
  const levelKey = `Level${levelIndex + 1}`;
  const scoreIconFallback = gutAssessmentImages[`resultScoreIcon${levelKey}`];
  const optionIcon1Fallback = gutAssessmentImages[`resultOptionIcon1${levelKey}`];
  const optionIcon2Fallback = gutAssessmentImages[`resultOptionIcon2${levelKey}`];

  // API image URLs from suggestion
  const scoreIconApi = overallSuggestion?.image_url || '';
  const optionIcon1Api = overallSuggestion?.hints?.[0]?.image_url || '';
  const optionIcon2Api = overallSuggestion?.hints?.[1]?.image_url || '';

  // Prepare chart data - short labels with line breaks for chart
  const SHORT_LABELS = {
    'สุขภาพการขับถ่าย': 'การขับถ่าย',
    'สุขภาพการย่อยอาหาร': 'การย่อย\nอาหาร',
    'สุขภาพผิวและภูมิคุ้มกัน': 'ผิวและ\nภูมิคุ้มกัน',
    'สุขภาพจิตใจและอารมณ์': 'จิตใจและ\nอารมณ์',
    'การควบคุมน้ำหนัก': 'การควบคุม\nน้ำหนัก',
  };
  const chartLabels = groupResults.map((g) => {
    const cleanName = g.name.replace(/\s*\(.*\)/, '').trim();
    return SHORT_LABELS[cleanName] || cleanName;
  });
  const chartScores = groupResults.map((g) => g.score);
  const chartMaxScores = groupResults.map((g) => g.maxScore);

  // Map group system_name to hint icon
  const HINT_ICONS = {
    'Excretion': gutAssessmentImages.resultHintExcretion,
    '(Digestion)': gutAssessmentImages.resultHintDigestion,
    ' สุขภาพผิวและภูมิคุ้มกัน': gutAssessmentImages.resultHintSkinImmunity,
    'Mind & Mood': gutAssessmentImages.resultHintMindMood,
    'Weight Management': gutAssessmentImages.resultHintWeight,
  };

  return (
    <div className="gut-result" data-testid="gut-result-page" style={{ background: getGradientByScore(suggestions, totalScore) }}>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1 }}>
        <img style={{ width: '100%', objectFit: 'fill' }} src={gutAssessmentImages.detailBg} />
      </div>
      <div className="gut-result__container" style={{ zIndex: 2, position: 'relative' }} data-testid="gut-result-container">
        <div className="gut-result__title" data-testid="gut-result-title">{title}</div>

        {/* Dropdown - History Selector */}
        {historyList.length > 1 && (
          <div className="gut-result__dropdown" data-testid="gut-result-dropdown" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
            <span className="gut-result__dropdown-text" data-testid="gut-result-dropdown-text">
              ผลการประเมิน {formatDate(historyList.find((h) => h._id === selectedAnswerId)?.createdDate || result?.updatedDate)}
            </span>
            <span className={`gut-result__dropdown-chevron ${isDropdownOpen ? 'gut-result__dropdown-chevron--open' : ''}`} />
            {isDropdownOpen && (
              <div className="gut-result__dropdown-menu" data-testid="gut-result-dropdown-menu">
                {[...historyList].sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate)).map((item) => (
                  <div
                    className={`gut-result__dropdown-item ${item._id === selectedAnswerId ? 'gut-result__dropdown-item--active' : ''}`}
                    key={item._id}
                    onClick={(e) => { e.stopPropagation(); handleSelectHistory(item._id); }}
                  >
                    ผลการประเมิน {formatDate(item.updatedDate || item.createdDate)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Score Card */}
        <div className="gut-result__score-card" data-testid="gut-result-score-card">
          <div className="gut-result__score-header" data-testid="gut-result-score-header">
            <div className="gut-result__score-label" data-testid="gut-result-score-label">
              {result.ListType?.scoreTitle || 'คะแนนของคุณ'}
            </div>
            <div className="gut-result__score-visual" data-testid="gut-result-score-visual">
              <ImageWithFallback className="gut-result__score-icon" data-testid="gut-result-score-icon" src={scoreIconApi} fallbackSrc={scoreIconFallback} alt="" />
              <div className="gut-result__score-value" data-testid="gut-result-score-value" style={{ color: levelColor }}>
                {totalScore}
              </div>
              {scoreDiff !== null && scoreDiff !== 0 && (
                <div className={`gut-result__score-diff ${scoreDiff > 0 ? 'gut-result__score-diff--up' : 'gut-result__score-diff--down'}`} data-testid="gut-result-score-diff">
                  <img className="gut-result__score-diff-arrow" src={scoreDiff > 0 ? gutAssessmentImages.resultScoreUp : gutAssessmentImages.resultScoreDown} alt="" />
                  <span className="gut-result__score-diff-value">{Math.abs(scoreDiff)}</span>
                </div>
              )}
            </div>
          </div>

          {overallSuggestion && (
            <>
              <div className="gut-result__score-level" data-testid="gut-result-score-level" style={{ color: levelColor }}>
                {overallSuggestion.head}
              </div>
              <div className="gut-result__score-range" data-testid="gut-result-score-range">
                <div className="gut-result__score-range-text" data-testid="gut-result-score-range-text" style={{ background: levelColor }}>
                  {scoreRange}
                </div>
              </div>
              <div className="gut-result__score-description" data-testid="gut-result-score-description">
                {overallSuggestion.detail}
              </div>

              {/* Option Cards */}
              {overallSuggestion?.hints?.map((hint, index) => (
                <div className="gut-result__option" data-testid={`gut-result-option-${index}`} key={index}>
                  <ImageWithFallback
                    className="gut-result__option-icon"
                    src={hint.image_url || ''}
                    fallbackSrc={index === 0 ? optionIcon1Fallback : optionIcon2Fallback}
                    alt=""
                  />
                  <div className="gut-result__option-content">
                    <div className="gut-result__option-title" data-testid={`gut-result-option-${index}-title`}>{hint.title}</div>
                    <div className="gut-result__option-detail" data-testid={`gut-result-option-${index}-detail`}>{hint.detail}</div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Chart Card */}
        <div className="gut-result__chart-card" data-testid="gut-result-chart-card">
          <div className="gut-result__chart-inner" data-testid="gut-result-chart-inner">
            <GutRadarChart
              labels={chartLabels}
              scores={chartScores}
              maxScores={chartMaxScores}
              color={levelColor}
            />
          </div>
        </div>

        {/* Hint Cards - Per Group */}
        {groupResults.map((group, index) => (
          <div className="gut-result__hint" data-testid={`gut-result-hint-${index}`} key={index}>
            <div className="gut-result__hint-header" data-testid={`gut-result-hint-header-${index}`} onClick={() => toggleHint(index)}>
              <ImageWithFallback className="gut-result__hint-icon" src={group.imageUrl} fallbackSrc={HINT_ICONS[group.systemName] || gutAssessmentImages.resultHintExcretion} alt="" />
              <div className="gut-result__hint-info">
                <div className="gut-result__hint-name" data-testid={`gut-result-hint-name-${index}`}>{group.name}</div>
                <div className="gut-result__hint-level" data-testid={`gut-result-hint-level-${index}`} style={{ color: group.color }}>
                  {group.level}
                </div>
              </div>
              <img
                className={`gut-result__hint-chevron ${expandedHints[index] ? 'gut-result__hint-chevron--open' : ''}`}
                src={gutAssessmentImages.resultChevronDown}
                alt=""
              />
            </div>
            {expandedHints[index] && (
              <div className="gut-result__hint-expand" data-testid={`gut-result-hint-expand-${index}`}>
                <div className="gut-result__hint-expand-title" style={{ color: group.color }}>{group.description}</div>
                <div className="gut-result__hint-expand-detail">{group.hint}</div>
              </div>
            )}
          </div>
        ))}

      </div>

      <div className="gut-result__footer" data-testid="gut-result-footer">
        <button className="gut-result__back-btn" data-testid="gut-result-btn-back" onClick={handleBack}>
          <img src={gutAssessmentImages.resultArrowLeft} alt="" />
          กลับสู่หน้าหลัก
        </button>
      </div>

      <DraggableFloat
        imageUrl={redirectImage ? `${process.env.REACT_APP_S3_URL}${redirectImage}` : ''}
        redirectUrl={redirectURL}
      />
    </div>
  );
};

export default GutAssessmentResult;
